import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import dynamic from 'next/dynamic'

import { PapBadgeRenderer } from '@thg-commerce/enterprise-components/PapOverlayBadge'
import { QubitUrgencyMessagingRendererProps } from '@thg-commerce/enterprise-components/Qubit/QubitUrgencyMessaging/QubitUrgencyMessagingRenderer'
import {
  useI18n,
  useSiteConfig,
  useTheme as useCoreTheme,
} from '@thg-commerce/enterprise-core'
import { ProductPageData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import { QubitViewType } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Qubit'
import {
  ProductImageGalleryStyle,
  styled,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { mq, spacing } from '@thg-commerce/enterprise-theme'
import { ImageGrid } from '@thg-commerce/gravity-patterns'
import { Orientation } from '@thg-commerce/gravity-patterns/ImageGallery/types'
import {
  BreakpointArray,
  mediaQueryRenderer,
} from '@thg-commerce/gravity-theme'

import { useProductPage } from '../../context'
import { ProductImageGalleryZoomPresenter } from '../ProductImageGalleryZoomPresenter'

import { ImageGalleryRenderer } from './ImageGalleryRenderer'

const PlaceholderImage = styled.div`
  aspect-ratio: ${(props) =>
    props.theme?.pageTheme?.imageGallery.grid?.imageAspectRatio || '1/1'};
  background-color: ${(props) => props.theme.colors.palette.greys.lighter};
  display: inline-block;
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    width: 50%;
  }
`

const StyledImageGalleryRenderer = styled(ImageGalleryRenderer)<{
  forceDisplay: boolean
}>`
  margin-bottom: 0;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin-bottom: ${spacing(4)};
  }
  ${(props) => {
    return !props.forceDisplay && props.theme.pageTheme
      ? mediaQueryRenderer(
          props.theme.pageTheme?.imageGallery.style,
          (style) => {
            return `&& { display: ${
              style === ProductImageGalleryStyle.CAROUSEL ? 'block' : 'none'
            };}`
          },
        )
      : ''
  }}
`

const StyledImageGrid = styled(ImageGrid)`
  margin-bottom: ${spacing(4)};
  ${(props) => {
    return props.theme.pageTheme
      ? mediaQueryRenderer(
          props.theme.pageTheme.imageGallery.style,
          (style) => {
            return `&& { display: ${
              style === ProductImageGalleryStyle.GRID ? 'grid' : 'none'
            }; }
            `
          },
        )
      : ''
  }}
`

const StyledPapWrapper = styled.div<{ renderGrid: boolean }>`
  position: relative;
  left: ${spacing(0)};
  ${(props) => !props.renderGrid && mq(props.theme.breakpointUtils.map, 'md')} {
    left: ${spacing(12)};
  }
`

const ImageGridDeferred = withHydrationOnDemand({
  on: ['visible'],
})(StyledImageGrid)

const QubitUrgencyMessagingRenderer = dynamic<
  QubitUrgencyMessagingRendererProps
>(
  import(
    '@thg-commerce/enterprise-components/Qubit/QubitUrgencyMessaging/QubitUrgencyMessagingRenderer'
  ).then((mod) => mod.QubitUrgencyMessagingRenderer),
)

const QubitUrgencyMessagingRendererDeferred = withHydrationOnDemand({
  on: ['idle'],
})(QubitUrgencyMessagingRenderer)

interface ImageGalleryProps {
  thumbnails: {
    url: string
    alt: string
    isVideoUrl?: boolean
    videoTitle?: string
    videoTranscript?: string
  }[]
  images: {
    url: string
    alt: string
    isVideoUrl?: boolean
    videoTitle?: string
    videoTranscript?: string
  }[]
  zoom: {
    url: string
    alt: string
    isVideoUrl?: boolean
    videoTitle?: string
    videoTranscript?: string
  }[]
}

const addVideoToGallery = (
  product: ProductPageData,
  gallery: ImageGalleryProps,
) => {
  if (!product.video) return gallery

  const videoUrl = `${product.video.link}.mp4`
  const videoExists = gallery.thumbnails.some(
    (item: any) => item.url === videoUrl,
  )

  if (!videoExists) {
    const videoTitle = product.video.title

    const videoTranscript = product.video.transcript
    const videoItem = {
      videoTitle,
      videoTranscript,
      url: videoUrl,
      alt: 'Product Demonstration Video',
      isVideoUrl: true,
    }
    gallery.thumbnails.push({
      ...videoItem,
      alt: 'Product demonstration video thumbnail',
    })
    gallery.zoom.push(videoItem)
    gallery.images.push(videoItem)
  }
  return gallery
}

const useProductDisplaySettings = (product, theme) => {
  const productHasManualRecommendations = React.useMemo(
    () => (product?.manualRecommendations || []).length > 0,
    [product],
  )

  const renderGrid = React.useMemo(
    () =>
      !productHasManualRecommendations && theme?.pageTheme
        ? theme.pageTheme.imageGallery.grid?.enabled
        : false,
    [productHasManualRecommendations, theme],
  )

  const renderCarousel = React.useMemo(
    () =>
      !productHasManualRecommendations && theme?.pageTheme
        ? theme.pageTheme.imageGallery.carousel?.enabled
        : true,
    [productHasManualRecommendations, theme],
  )

  const desktopUrgencyMessagingVerticalAlignment = React.useMemo(() => {
    if (renderGrid) {
      return theme?.pageTheme?.imageGallery?.grid
        ?.urgencyMessagingVerticalPlacement
    }
    return theme?.pageTheme?.imageGallery?.carousel
      ?.urgencyMessagingVerticalPlacement
  }, [renderGrid, theme])

  return {
    productHasManualRecommendations,
    renderGrid,
    renderCarousel,
    desktopUrgencyMessagingVerticalAlignment,
  }
}

export const ProductImageGalleryRenderer: React.FunctionComponent = () => {
  const {
    product,
    selectedVariant,
    presentImageGalleryZoomModal,
  } = useProductPage()
  const { enablePDPGalleryZoom, enablePDPGalleryZoomDesktop } = useSiteConfig()
  const {
    icons: { zoomIn },
  } = useTheme()
  const theme = useTheme()
  const coreTheme = useCoreTheme()
  const i18n = useI18n()

  let gallery = selectedVariant?.gallery || product?.gallery

  const {
    productHasManualRecommendations,
    renderGrid,
    renderCarousel,
    desktopUrgencyMessagingVerticalAlignment,
  } = useProductDisplaySettings(product, theme)

  if (!gallery) {
    return <PlaceholderImage />
  }

  const i18nText = {
    zoomInLabel: i18n('imagegallery.controls.zoomin.arialabel'),
    zoomOutLabel: i18n('imagegallery.controls.zoomout.arialabel'),
    closeI18nText: {
      closeAriaLabel: i18n('general.modal.close.button.arialabel'),
      closeLabel: i18n('general.modal.close.button.label'),
    },
  }

  const orientation: BreakpointArray<Orientation> = [
    Orientation.VERTICAL,
    Orientation.VERTICAL,
    Orientation.HORIZONTAL,
    Orientation.HORIZONTAL,
  ]

  gallery = addVideoToGallery(product, gallery)

  const qubitBadgeMessagingStyle = theme?.pageTheme?.marketingBadgeStyle || ''

  return (
    <div style={{ position: 'relative' }}>
      <StyledPapWrapper renderGrid={renderGrid}>
        <PapBadgeRenderer
          badgeStyle={coreTheme.widget.productList.productItemOverlay}
          marketedSpecialOfferTitleText={
            product.marketedSpecialOffer?.title || ''
          }
        />
      </StyledPapWrapper>
      <QubitUrgencyMessagingRendererDeferred
        productId={`${product?.sku}`}
        type={QubitViewType.PRODUCT}
        isGrid={renderGrid || false}
        isFullWidth={gallery.images.length > 1}
        orientation={orientation}
        horizontalAlignment="left"
        verticalAlignment={{
          desktop: desktopUrgencyMessagingVerticalAlignment,
          mobile:
            theme?.pageTheme?.imageGallery
              ?.urgencyMessagingVerticalPlacementMobile,
        }}
        qubitBadgeMessagingStyle={qubitBadgeMessagingStyle}
      />
      {renderGrid ? (
        <React.Fragment>
          <ImageGridDeferred
            controlIcons={{
              zoomIn: zoomIn?.svgPath,
              fill: theme.colors.palette.greys.dark,
            }}
            zoomOnClickHandler={(imageIndex) =>
              presentImageGalleryZoomModal?.current(imageIndex)
            }
            gapSpacing={theme?.pageTheme?.imageGallery.grid?.gapSpacing || 1}
            columnCount={2}
            images={gallery.images || []}
            imageAspectRatio={
              theme?.pageTheme?.imageGallery.grid?.imageAspectRatio
            }
            zoomEnabled={enablePDPGalleryZoom}
            desktopZoomEnabled={enablePDPGalleryZoomDesktop}
            tagStyle={theme.pageTheme.imageGallery.tagStyle}
            i18nText={i18nText}
          />
          <ProductImageGalleryZoomPresenter />
        </React.Fragment>
      ) : null}
      {renderCarousel ? (
        <StyledImageGalleryRenderer
          forceDisplay={productHasManualRecommendations}
        />
      ) : null}
    </div>
  )
}
